import { CustomerPortalInputType } from '@/constants'

// Maps an OMP route to the customer-portal step type the backend fires on entry.
// Home 30, Orders 31, Subscriptions 32, SubscriptionDetail 33, PersonalInfo 34
// (the account/address/payment page), OrderDetail 35.
export function inputTypeForPath(pathname: string): number | undefined {
  if (/^\/portal\/orders\/[^/]+/.test(pathname)) {
    return CustomerPortalInputType.CustomerPortalOrderDetail
  }
  if (pathname.startsWith('/portal/orders')) {
    return CustomerPortalInputType.CustomerPortalOrders
  }
  if (/^\/portal\/subscriptions\/[^/]+/.test(pathname)) {
    return CustomerPortalInputType.CustomerPortalSubscriptionDetail
  }
  if (pathname.startsWith('/portal/subscriptions')) {
    return CustomerPortalInputType.CustomerPortalSubscriptions
  }
  if (pathname.startsWith('/portal/account')) {
    return CustomerPortalInputType.CustomerPortalPersonalInfo
  }
  if (pathname === '/portal' || pathname === '/portal/') {
    return CustomerPortalInputType.CustomerPortalHome
  }
  return undefined
}

// Detail routes carry an item id, so re-entering them must re-submit to move the
// server's selected item. Every other portal route is item-agnostic.
export function isItemScopedPath(pathname: string): boolean {
  return /^\/portal\/(orders|subscriptions)\/[^/]+/.test(pathname)
}

// Reverse map used after an undo to sync the URL to the restored step.
// Detail steps are omitted — they need an id, so we keep the URL the browser restored.
export function pathForStepType(stepType: number | string | undefined): string | undefined {
  switch (Number(stepType)) {
    case CustomerPortalInputType.CustomerPortalHome:
      return '/portal'
    case CustomerPortalInputType.CustomerPortalOrders:
      return '/portal/orders'
    case CustomerPortalInputType.CustomerPortalSubscriptions:
      return '/portal/subscriptions'
    case CustomerPortalInputType.CustomerPortalPersonalInfo:
      return '/portal/account'
    default:
      return undefined
  }
}

// Entry route for a portal session (preview links / deep links / OMP start).
// Unlike pathForStepType, this resolves detail pages too, pulling the id from
// the session's selected_item; falls back to the list page when it's absent.
export function portalEntryPath(session: any): string {
  const stepType = Number(session?.step_type)
  const selected = session?.selected_item
  switch (stepType) {
    case CustomerPortalInputType.CustomerPortalOrders:
      return '/portal/orders'
    case CustomerPortalInputType.CustomerPortalSubscriptions:
      return '/portal/subscriptions'
    case CustomerPortalInputType.CustomerPortalPersonalInfo:
      return '/portal/account'
    case CustomerPortalInputType.CustomerPortalOrderDetail: {
      const orderId = selected?.order?.id ?? selected?.order_id
      return orderId != null ? `/portal/orders/${orderId}` : '/portal/orders'
    }
    case CustomerPortalInputType.CustomerPortalSubscriptionDetail: {
      const subId = selected?.id
      return subId != null ? `/portal/subscriptions/${subId}` : '/portal/subscriptions'
    }
    case CustomerPortalInputType.CustomerPortalHome:
    default:
      return '/portal'
  }
}

// The input to submit lives in side_menu_inputs for top-level pages, or in
// valid_inputs/additional_inputs for step-specific ones like OrderDetail.
export function findPortalInput(session: any, type: number): any | undefined {
  const pools = [session?.side_menu_inputs, session?.valid_inputs, session?.additional_inputs]
  for (const pool of pools) {
    if (Array.isArray(pool)) {
      const found = pool.find((i: any) => Number(i?.type) === type)
      if (found) {
        return found
      }
    }
  }
  return undefined
}
